import { AttractionThemeDropdown } from '@/components/dropdowns/AttractionThemeDropdown';
import { useApiClient } from '@startupquickstart/frontend';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

interface TourAttractionThemeCellProps {
  tourId: string;
  attractionId: string;
  currentThemeId?: string | null;
  currentThemeName?: string | null;
  themes?: Array<{
    id: string;
    name?: string | null;
    themeType?: {
      id: string;
      name: string;
      slug: string;
    };
  }>;
}

/**
 * Inline editable theme selector for tour attractions
 * Shows current theme or "Tour default", allows changing via dropdown
 */
export function TourAttractionThemeCell({
  tourId,
  attractionId,
  currentThemeId,
  currentThemeName,
  themes,
}: TourAttractionThemeCellProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);

  const handleThemeChange = async (newThemeId: string | null) => {
    setIsUpdating(true);
    try {
      await apiClient.put(`/tours/${tourId}/attractions/${attractionId}`, {
        attractionThemeId: newThemeId,
      });

      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['Tour'] });
      queryClient.invalidateQueries({ queryKey: ['Attraction'] });

      toast.success(
        newThemeId ? 'Theme updated successfully' : 'Reset to tour default'
      );
    } catch (error) {
      console.error('Error updating attraction theme:', error);
      toast.error('Failed to update theme');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="w-48">
      <AttractionThemeDropdown
        attractionId={attractionId}
        value={currentThemeId}
        onChange={handleThemeChange}
        placeholder={currentThemeName || 'Tour default'}
        disabled={isUpdating}
        className="w-full"
        themes={themes}
      />
    </div>
  );
}
